import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Utensils, 
  Sparkles, 
  Loader2,
  Flame,
  Beef,
  Wheat,
  Droplet,
  Clock,
  ChevronRight
} from 'lucide-react';

export default function NutritionPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiParams, setAiParams] = useState({
    goal: 'maintenance',
    calories: 2000,
    dietary_restrictions: ''
  });

  const fetchMealPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/meal-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMealPlans(response.data);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMealPlans();
  }, [fetchMealPlans]);

  const generateAIMealPlan = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/meal-plans/ai-generate`,
        null,
        {
          params: aiParams,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('AI meal plan generated!');
      setShowAIDialog(false);
      setMealPlans(prev => [response.data, ...prev]);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate meal plan';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="nutrition-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            NUTRITION <span className="text-primary">PLANS</span>
          </h1>
          <p className="text-gray-400 mt-1">Fuel your body for peak performance</p>
        </div>
        
        <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
          <DialogTrigger asChild>
            <Button 
              data-testid="ai-meal-plan-btn"
              className="bg-primary hover:bg-red-700 text-white uppercase font-semibold"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Generate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-white uppercase">
                AI Meal Plan Builder
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Goal</Label>
                <Select 
                  value={aiParams.goal} 
                  onValueChange={(v) => setAiParams({...aiParams, goal: v})}
                >
                  <SelectTrigger className="bg-secondary border-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weight_loss">Weight Loss</SelectItem>
                    <SelectItem value="muscle_gain">Muscle Gain</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Daily Calories</Label>
                <Select 
                  value={String(aiParams.calories)} 
                  onValueChange={(v) => setAiParams({...aiParams, calories: parseInt(v)})}
                >
                  <SelectTrigger className="bg-secondary border-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1500">1500 kcal</SelectItem>
                    <SelectItem value="1800">1800 kcal</SelectItem>
                    <SelectItem value="2000">2000 kcal</SelectItem>
                    <SelectItem value="2200">2200 kcal</SelectItem>
                    <SelectItem value="2500">2500 kcal</SelectItem>
                    <SelectItem value="3000">3000 kcal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Dietary Restrictions</Label>
                <Select 
                  value={aiParams.dietary_restrictions || "none"} 
                  onValueChange={(v) => setAiParams({...aiParams, dietary_restrictions: v === "none" ? "" : v})}
                >
                  <SelectTrigger className="bg-secondary border-transparent">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="vegetarian">Vegetarian</SelectItem>
                    <SelectItem value="vegan">Vegan</SelectItem>
                    <SelectItem value="gluten_free">Gluten-Free</SelectItem>
                    <SelectItem value="dairy_free">Dairy-Free</SelectItem>
                    <SelectItem value="keto">Keto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                data-testid="generate-meal-plan-btn"
                onClick={generateAIMealPlan}
                disabled={generating}
                className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold mt-4"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Meal Plan
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Meal Plans Grid */}
      {mealPlans.length === 0 ? (
        <div className="text-center py-12 card-tactical">
          <Utensils className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No meal plans yet</h3>
          <p className="text-gray-400 mb-4">Generate your first AI meal plan to get started!</p>
          <Button 
            onClick={() => setShowAIDialog(true)}
            className="bg-primary hover:bg-red-700"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Meal Plan
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mealPlans.map((plan) => (
            <MealPlanCard key={plan.id} plan={plan} onView={() => navigate(`/nutrition/${plan.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

const MealPlanCard = ({ plan, onView }) => {
  const getGoalColor = (goal) => {
    switch (goal) {
      case 'weight_loss': return 'text-orange-500 bg-orange-500/10';
      case 'muscle_gain': return 'text-blue-500 bg-blue-500/10';
      case 'maintenance': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getGoalLabel = (goal) => {
    switch (goal) {
      case 'weight_loss': return 'Weight Loss';
      case 'muscle_gain': return 'Muscle Gain';
      case 'maintenance': return 'Maintenance';
      default: return goal;
    }
  };

  return (
    <div className="card-tactical group cursor-pointer" onClick={onView}>
      <div className="flex items-start justify-between mb-4">
        <div className={`px-2 py-1 rounded-sm text-xs uppercase font-semibold ${getGoalColor(plan.goal)}`}>
          {getGoalLabel(plan.goal)}
        </div>
        {plan.is_ai_generated && (
          <div className="flex items-center gap-1 text-primary text-xs">
            <Sparkles className="w-3 h-3" />
            AI
          </div>
        )}
      </div>
      
      <h3 className="font-heading text-xl text-white mb-2">{plan.title}</h3>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{plan.description}</p>
      
      {/* Macros */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <div className="text-center p-2 bg-secondary/50 rounded-sm">
          <Flame className="w-4 h-4 text-orange-500 mx-auto mb-1" />
          <div className="font-mono text-sm text-white">{plan.calories_target}</div>
          <div className="text-xs text-gray-500">kcal</div>
        </div>
        <div className="text-center p-2 bg-secondary/50 rounded-sm">
          <Beef className="w-4 h-4 text-red-500 mx-auto mb-1" />
          <div className="font-mono text-sm text-white">{plan.protein_g}g</div>
          <div className="text-xs text-gray-500">protein</div>
        </div>
        <div className="text-center p-2 bg-secondary/50 rounded-sm">
          <Wheat className="w-4 h-4 text-yellow-500 mx-auto mb-1" />
          <div className="font-mono text-sm text-white">{plan.carbs_g}g</div>
          <div className="text-xs text-gray-500">carbs</div>
        </div>
        <div className="text-center p-2 bg-secondary/50 rounded-sm">
          <Droplet className="w-4 h-4 text-blue-500 mx-auto mb-1" />
          <div className="font-mono text-sm text-white">{plan.fats_g}g</div>
          <div className="text-xs text-gray-500">fats</div>
        </div>
      </div>
      
      <Button 
        data-testid={`view-meal-plan-${plan.id}`}
        className="w-full bg-secondary hover:bg-primary group-hover:bg-primary transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          onView();
        }}
      >
        View Plan
        <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};
